import * as React from "react";
import Header from "./Header";
import { Body1, makeStyles, Tab, TabList, tokens } from "@fluentui/react-components";
import { useFeatureFlagEnabled } from "posthog-js/react";
import AnalysisTab from "./AnalysisTab";
import TimelineTab from "./TimelineTab";
import EventsTab from "./EventsTab";
import { useLatestEvent, useEvent } from "../../lib/eventing/useEvent";
import { AppEventBus } from "../../services/appEventBus";
import { AlertEntry } from "../../services/workbookAnalyticsRulesEngine";
import { rulesEngine } from "../index";
import { ConfigStore } from "../../lib/config/config";

export interface TaskPaneSettings {
  selectedTab?: string;
  filterType?: 'workbook' | 'worksheet';
}

interface AppProps {
  title: string;
  settingsStore: ConfigStore<TaskPaneSettings>;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  panel: {
    margin: "16px",
  },
  tabContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0, // Important for flex scrolling
  },
  tabList: {
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  tabContent: {
    flex: 1,
    minHeight: 0, // Important for flex scrolling
    overflow: "hidden",
    padding: 0,
    margin: 0
  }
});

const TaskPane: React.FC<AppProps> = (props: AppProps) => {
  const styles = useStyles();
  const { settingsStore } = props;
  const coachEnabled = useFeatureFlagEnabled('aiCoachAvailable');
  const debugEnabled = useFeatureFlagEnabled('aiCoachDebug');
  const [selectedTab, setSelectedTab] = React.useState<string>("analysis");
  const [filterType, setFilterType] = React.useState<'workbook' | 'worksheet'>('worksheet');
  const [classifiedWorksheetIds, setClassifiedWorksheetIds] = React.useState<Set<string>>(new Set());
  const [currentWorksheetId, setCurrentWorksheetId] = React.useState<string | undefined>();
  const [alerts, setAlerts] = React.useState<Record<string, AlertEntry>>(() => {
    const currentAlerts = rulesEngine.alerts;
    return currentAlerts.reduce((acc, alert) => {
      acc[alert.id] = alert;
      return acc;
    }, {} as Record<string, AlertEntry>);
  });

  React.useEffect(() => {
    settingsStore.load().then((settings) => {
      if (settings) {
        if (settings.selectedTab) setSelectedTab(settings.selectedTab);
        if (settings.filterType) setFilterType(settings.filterType);
      }
    });
  }, [settingsStore]);

  const saveSettings = (updates: Partial<TaskPaneSettings>) => {
    settingsStore.load().then((currentSettings) => {
      settingsStore.save({ ...currentSettings, ...updates });
    });
  };

  useEvent<AppEventBus>(bus => bus.namespaces.excel.events.worksheetActivated.on((payload) => {
      setCurrentWorksheetId(payload.worksheet.id);
  }));

  useEvent<AppEventBus>(bus => {
      const subRaised = bus.namespaces.alert.events.alertRaised.on((alert) => {
        setAlerts((prev) => ({ ...prev, [alert.id]: alert }));
      });
      const subResolved = bus.namespaces.alert.events.alertResolved.on((alert) => {
        setAlerts((prev) => {
            const next = { ...prev };
            delete next[alert.id];
            return next;
        });
      });
      const subDismissed = bus.namespaces.alert.events.alertDismissed.on((alert) => {
        setAlerts((prev) => ({ ...prev, [alert.id]: alert }));
      });
      const subRestored = bus.namespaces.alert.events.alertRestored.on((alert) => {
        setAlerts((prev) => ({ ...prev, [alert.id]: alert }));
      });
      return {
          id: 'alert-subscription',
          unsubscribe: () => {
              subRaised.unsubscribe();
              subResolved.unsubscribe();
              subDismissed.unsubscribe();
              subRestored.unsubscribe();
              return true;
          }
      }
  });

  useEvent<AppEventBus>(bus => bus.namespaces.worksheetClassification.events.worksheetClassified.on((payload) => {
      setClassifiedWorksheetIds(prev => {
          const next = new Set(prev);
          next.add(payload.worksheetId);
          return next;
      });
  }));

  useEvent<AppEventBus>(bus => bus.namespaces.worksheetClassification.events.worksheetUnclassified.on((payload) => {
      setClassifiedWorksheetIds(prev => {
          const next = new Set(prev);
          next.delete(payload.worksheetId);
          return next;
      });
  }));

    const currentWorkbook = useLatestEvent<
      { workbookCode: string },
      AppEventBus
    >(
      (bus, listener) => bus.namespaces.workbookClassification.events.workbookClassified.on((payload) => {
        listener({ workbookCode: payload.code });
      })
    );

  const allActiveAlerts = Object.values(alerts).filter((alert) => !alert.dismissal);
  const currentSheetAlertsCount = currentWorksheetId 
    ? allActiveAlerts.filter(a => a.sheetId === currentWorksheetId).length 
    : 0;
  const otherSheetAlertsCount = allActiveAlerts.length - currentSheetAlertsCount;
  
  const thisSheetText = currentSheetAlertsCount ? `${currentSheetAlertsCount} items on this sheet` : '';
  const otherSheetsText = otherSheetAlertsCount
    ? thisSheetText
      ? `, ${otherSheetAlertsCount} on others`
      : `${otherSheetAlertsCount} items on other sheets`
    : '';
  const headerMessage = currentWorksheetId 
    ? `${thisSheetText}${otherSheetsText}` || 'No items need attention'
    : allActiveAlerts.length
      ? `${allActiveAlerts.length} items need attention`
      : 'No items need attention';
  const handleTabSelect = (tab: string) => {
    setSelectedTab(tab);
    saveSettings({ selectedTab: tab });
  };

  const handleFilterChange = (type: 'workbook' | 'worksheet') => {
    setFilterType(type);
    saveSettings({ filterType: type });
  };

  React.useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      if (!debugEnabled) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [debugEnabled]);

  return (
    <div className={styles.root}>
      <Header 
        logo="assets/icon-512x512.png" 
        title={props.title} 
        message={headerMessage} 
        filterType={filterType}
        onFilterChange={handleFilterChange}
      />
      { coachEnabled
        ? !currentWorkbook?.workbookCode 
          ? <div className={styles.panel}>
              <Body1>Aμto doesn't recognize this workbook yet, so no insights are available.</Body1>
            </div>
          : <div className={styles.tabContainer}>
              <div className={styles.tabList}>
                <TabList selectedValue={selectedTab} onTabSelect={(_, data) => handleTabSelect(data.value as string)}>
                  <Tab value="analysis">Analysis</Tab>
                  <Tab value="timeline">Timeline</Tab>
                  {debugEnabled && <>
                    <Tab value="events">Events</Tab>
                    <Tab value="data">Data</Tab>
                  </>}
                </TabList>
              </div>
              <div className={styles.tabContent}>
                {selectedTab === "analysis" && (
                  <AnalysisTab 
                    classifiedWorksheetIds={classifiedWorksheetIds} 
                    alerts={alerts} 
                    currentWorksheetId={currentWorksheetId}
                    filterType={filterType}
                    onFilterChange={handleFilterChange}
                  />
                )}
                {selectedTab === "timeline" && <TimelineTab />}
                {selectedTab === "events" && <EventsTab />}
                {selectedTab === "data" && <div>Data Tab Content</div>}
              </div>
            </div>
        : <div className={styles.panel}>
            <Body1>The Aμto Coach feature is not yet available for your account.</Body1>
          </div>
      }
    </div>
  );
};

export default TaskPane;
